import React, { useState, useEffect, useCallback, useRef } from "react";
import { Clock, AlertTriangle, LogOut, RefreshCw } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SESSION_WARNING_BEFORE_MS = 5 * 60 * 1000; // Show warning 5 min before timeout

export const SessionManager = ({ auth, onLogout, timeoutMinutes = 30 }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const sessionCheckFailuresRef = useRef(0);

  const timeoutMs = timeoutMinutes * 60 * 1000;

  const resetTimers = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);

    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    // Set warning timer
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(Math.floor(SESSION_WARNING_BEFORE_MS / 1000));
      // Start countdown
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, timeoutMs - SESSION_WARNING_BEFORE_MS);

    // Set logout timer
    logoutTimerRef.current = setTimeout(() => {
      handleSessionExpired();
    }, timeoutMs);
  }, [timeoutMs]);

  const handleSessionExpired = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    onLogout("session_timeout");
  }, [onLogout]);

  const handleStayActive = () => {
    resetTimers();
  };

  // Track user activity
  useEffect(() => {
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    const onActivity = () => {
      // Only reset if warning isn't showing (prevents accidental dismissal)
      if (!showWarning) {
        lastActivityRef.current = Date.now();
        resetTimers();
      }
    };
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    resetTimers();

    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [resetTimers, showWarning]);

  // Check session validity on mount and periodically (cookie-based + header fallback for cross-site prod)
  useEffect(() => {
    const checkSession = async () => {
      try {
        const headers = {};
        // Header fallback: if HttpOnly cookie doesn't flow (cross-site prod), use the in-memory token
        if (auth?.token) headers["X-Admin-Auth"] = auth.token;
        const res = await fetch(`${API_URL}/api/auth/validate-session`, {
          credentials: 'include',
          headers,
        });
        if (res.ok) {
          sessionCheckFailuresRef.current = 0;
          return;
        }
        if (res.status === 401 || res.status === 403) {
          // Only logout after 2 consecutive failures to avoid flaky-network false positives
          sessionCheckFailuresRef.current += 1;
          if (sessionCheckFailuresRef.current >= 2) {
            onLogout("token_expired");
          }
        }
      } catch {
        // Network error — don't logout, might be offline
      }
    };
    checkSession();
    const interval = setInterval(checkSession, 10 * 60 * 1000); // Check every 10 min
    return () => clearInterval(interval);
  }, [onLogout, auth?.token]);

  const formatCountdown = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  if (!showWarning) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="session-timeout-warning">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 animate-in fade-in">
        <div className="text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock size={32} className="text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-[#1E1B4B] mb-2">Session Expiring Soon</h2>
          <p className="text-[#475569] mb-4">
            Your session will expire due to inactivity.
          </p>
          <div className="text-3xl font-mono font-bold text-amber-600 mb-6" data-testid="session-countdown">
            {formatCountdown(countdown)}
          </div>
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleStayActive}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white rounded-xl font-semibold hover:opacity-90 transition-opacity"
              data-testid="session-stay-active-btn"
            >
              <RefreshCw size={18} />
              Stay Active
            </button>
            <button
              onClick={() => onLogout("manual")}
              className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-[#475569] rounded-xl font-semibold hover:bg-gray-200 transition-colors"
              data-testid="session-logout-btn"
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionManager;
